import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cx } from '../lib/cx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Tri-state: `true` shows a dash, not a tick (select-all with a partial set). */
  indeterminate?: boolean;
  /** Optional visible label; when omitted, pass `aria-label`. */
  children?: ReactNode;
}

/**
 * Real `<input type="checkbox">` (a11y §8.6.2), visually restyled, with a proper
 * indeterminate state for select-all. The input carries a 24px hit area over the
 * 15px visual box (WCAG 2.5.8).
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, className, children, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (innerRef.current !== null) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const setRefs = (node: HTMLInputElement | null): void => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref !== null) ref.current = node;
  };

  return (
    <label className={cx('ds-check', className)}>
      <input ref={setRefs} type="checkbox" className="ds-check__input" {...rest} />
      <span className="ds-check__box" aria-hidden="true">
        {indeterminate ? '–' : '✓'}
      </span>
      {children !== undefined ? <span>{children}</span> : null}
    </label>
  );
});
